import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative" #dropdownContainer>
      <!-- Trigger Button -->
      <div 
        (click)="toggleDropdown()"
        class="cursor-pointer">
        <ng-content select="[slot=trigger]"></ng-content>
      </div>

      <!-- Dropdown Menu -->
      <div 
        *ngIf="isOpen"
        [class]="getDropdownClasses()"
        class="absolute z-[20000] bg-white border border-gray-200 rounded-2xl shadow-lg py-2 min-w-[200px]"
        #dropdownMenu>
        <ng-content select="[slot=content]"></ng-content>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }

    .dropdown-enter {
      opacity: 0;
      transform: scale(0.95) translateY(-10px);
    }

    .dropdown-enter-active {
      opacity: 1;
      transform: scale(1) translateY(0);
      transition: all 0.2s ease-out;
    }

    .dropdown-leave {
      opacity: 1;
      transform: scale(1) translateY(0);
    }

    .dropdown-leave-active {
      opacity: 0;
      transform: scale(0.95) translateY(-10px);
      transition: all 0.15s ease-in;
    }

    /* Dark Mode Styles */
    :host-context(.dark),
    :host-context([data-theme='dark']),
    :host-context(html.dark),
    :host-context(.dark-mode) {
      .bg-white {
        background-color: #1e1e1e !important;
      }

      .border-gray-200 {
        border-color: #404040 !important;
      }

      .shadow-lg {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3) !important;
      }
    }

    /* Dark mode text color overrides for dropdown content */
    :host-context(.dark) ::ng-deep,
    :host-context([data-theme='dark']) ::ng-deep,
    :host-context(html.dark) ::ng-deep,
    :host-context(.dark-mode) ::ng-deep {
      .text-theme-text-primary {
        color: #e5e5e5 !important;
      }

      .text-theme-text-secondary {
        color: #a3a3a3 !important;
      }

      .hover\\:bg-theme-bg-hover:hover {
        background-color: #2a2a2a !important;
      }

      .bg-theme-bg-active {
        background-color: rgba(124, 58, 237, 0.2) !important;
      }

      .border-light {
        border-color: #404040 !important;
      }
    }
  `]
})
export class DropdownComponent {
  @Input() isOpen = false;
  @Input() position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' = 'bottom-right';
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() dropdownToggle = new EventEmitter<boolean>();

  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;
  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
    this.dropdownToggle.emit(this.isOpen);
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.isOpenChange.emit(this.isOpen);
      this.dropdownToggle.emit(this.isOpen);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.dropdownContainer && 
        !this.dropdownContainer.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }

  public getDropdownClasses(): string {
    const baseClasses = 'mt-2';
    
    switch (this.position) {
      case 'bottom-left':
        return `${baseClasses} left-0`;
      case 'bottom-right':
        return `${baseClasses} right-0`;
      case 'top-left':
        return `${baseClasses} bottom-full mb-2 left-0`;
      case 'top-right':
        return `${baseClasses} bottom-full mb-2 right-0`;
      default:
        return `${baseClasses} right-0`;
    }
  }
}
